import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, Users, Sparkles, Phone, MessageCircle, MapPin, Eye, TrendingUp, 
  Plus, Search, Filter, StickyNote, CheckCircle, Clock, XCircle, Star,
  ChevronRight, Loader2, Send, BookOpen, Bot, Lightbulb, Target, RefreshCw,
  Edit, Mail, Key, Save, X
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../utils/api';


// Get auth token from localStorage
function getAuthHeaders() {
  var token = localStorage.getItem('auth_token');
  return token ? { Authorization: 'Bearer ' + token } : {};
}

// Contact status options
var STATUS_OPTIONS = [
  { value: 'new', label: 'Sin contactar', color: 'slate' },
  { value: 'contacted', label: 'Contactado', color: 'blue' },
  { value: 'interested', label: 'Interesado', color: 'amber' },
  { value: 'negotiating', label: 'Negociando', color: 'purple' },
  { value: 'client', label: 'Cliente', color: 'emerald' },
  { value: 'rejected', label: 'Rechazado', color: 'red' }
];

var MODALITY_LABELS = {
  'residencial': 'Residencial',
  'ambulatorio': 'Ambulatorio',
  'online': 'Online',
  'mixto': 'Mixto'
};

export function SalesDashboard() {
  var [activeTab, setActiveTab] = useState('contacts');
  var [contacts, setContacts] = useState({ centers: [], professionals: [], workshops: [] });
  var [loading, setLoading] = useState(true);
  var [searchTerm, setSearchTerm] = useState('');
  var [filterType, setFilterType] = useState('all');
  var [filterStatus, setFilterStatus] = useState('all');
  var [filterRegion, setFilterRegion] = useState('all');
  var [selectedContact, setSelectedContact] = useState(null);
  var [showContactModal, setShowContactModal] = useState(false);
  var [stats, setStats] = useState(null);

  useEffect(function() {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      var headers = getAuthHeaders();
      var [centersRes, professionalsRes, workshopsRes, statsRes] = await Promise.all([
        axios.get(API + '/sales/contacts/centers', { headers: headers }),
        axios.get(API + '/sales/contacts/professionals', { headers: headers }),
        axios.get(API + '/sales/contacts/workshops', { headers: headers }),
        axios.get(API + '/sales/stats', { headers: headers }).catch(function() { return { data: {} }; })
      ]);
      setContacts({
        centers: centersRes.data.contacts || [],
        professionals: professionalsRes.data.contacts || [],
        workshops: workshopsRes.data.contacts || []
      });
      setStats(statsRes.data);
    } catch (e) {
      console.error(e);
      toast.error('Error cargando datos');
    }
    setLoading(false);
  }

  // Get all contacts merged
  function getAllContacts() {
    var all = [];
    contacts.centers.forEach(function(c) { all.push({ ...c, type: 'center' }); });
    contacts.professionals.forEach(function(p) { all.push({ ...p, type: 'professional' }); });
    contacts.workshops.forEach(function(w) { all.push({ ...w, type: 'workshop' }); });
    return all;
  }

  // Filter contacts
  function getFilteredContacts() {
    var all = getAllContacts();
    
    return all.filter(function(contact) {
      // Search filter
      if (searchTerm) {
        var search = searchTerm.toLowerCase();
        var name = (contact.name || '').toLowerCase();
        var contactName = (contact.contact_name || '').toLowerCase();
        var city = (contact.city || '').toLowerCase();
        if (!name.includes(search) && !contactName.includes(search) && !city.includes(search)) {
          return false;
        }
      }
      
      // Type filter
      if (filterType !== 'all' && contact.type !== filterType) {
        return false;
      }
      
      // Status filter
      if (filterStatus !== 'all' && (contact.sales_status || 'new') !== filterStatus) {
        return false;
      }
      
      // Region filter
      if (filterRegion !== 'all' && contact.region !== filterRegion) {
        return false;
      }
      
      return true;
    });
  }

  // Get unique regions
  function getRegions() {
    var regions = new Set();
    getAllContacts().forEach(function(c) {
      if (c.region) regions.add(c.region);
    });
    return Array.from(regions).sort();
  }

  function openContactDetail(contact) {
    setSelectedContact(contact);
    setShowContactModal(true);
  }

  function getStatusColor(status) {
    var opt = STATUS_OPTIONS.find(function(s) { return s.value === status; });
    return opt ? opt.color : 'slate';
  }

  function getStatusLabel(status) {
    var opt = STATUS_OPTIONS.find(function(s) { return s.value === status; });
    return opt ? opt.label : 'Sin contactar';
  }

  function formatWhatsAppLink(phone, center) {
    if (!phone) return null;
    var cleaned = phone.replace(/\D/g, '');
    if (!cleaned.startsWith('56')) {
      cleaned = '56' + cleaned;
    }
    
    // If no center provided, return simple link
    if (!center || !center.center_id) {
      return 'https://wa.me/' + cleaned;
    }
    
    // Generate center URL
    var slug = center.slug || center.center_id;
    var centerUrl = 'https://sinadicciones.org/centros/' + slug;
    
    // Predefined message for sales (no emojis to avoid encoding issues)
    var message = 'Hola, ¿cómo estás? Te escribimos desde SinAdicciones.org, el directorio de centros de rehabilitación y profesionales en Chile.\n\n' +
      'Hemos incluido tu centro en nuestra plataforma para que personas en búsqueda de ayuda te encuentren más rápido.\n\n' +
      'Aquí puedes ver tu ficha: ' + centerUrl + '\n\n' +
      'Si quieres revisar o actualizar tus datos para destacar más información (horarios, servicios, fotos o contacto), podemos ayudarte con eso. ¿Te interesa?';
    
    return 'https://wa.me/' + cleaned + '?text=' + encodeURIComponent(message);
  }

  var filteredContacts = getFilteredContacts();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <Header />
      
      <main className="container mx-auto px-4 pt-24 pb-16 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Panel de Ventas</h1>
            <p className="text-slate-400">CRM para gestión de contactos</p>
          </div>
          <Button onClick={loadData} variant="outline" className="border-slate-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <Card className="bg-slate-900 border-0">
            <CardContent className="p-4 text-center">
              <Building2 className="w-6 h-6 mx-auto mb-2 text-emerald-400" />
              <p className="text-2xl font-bold">{contacts.centers.length}</p>
              <p className="text-xs text-slate-500">Centros</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-0">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 mx-auto mb-2 text-blue-400" />
              <p className="text-2xl font-bold">{contacts.professionals.length}</p>
              <p className="text-xs text-slate-500">Profesionales</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-0">
            <CardContent className="p-4 text-center">
              <Sparkles className="w-6 h-6 mx-auto mb-2 text-purple-400" />
              <p className="text-2xl font-bold">{contacts.workshops.length}</p>
              <p className="text-xs text-slate-500">Talleres</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-0">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 mx-auto mb-2 text-slate-400" />
              <p className="text-2xl font-bold">{getAllContacts().filter(function(c) { return !c.sales_status || c.sales_status === 'new'; }).length}</p>
              <p className="text-xs text-slate-500">Sin contactar</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-0">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 mx-auto mb-2 text-amber-400" />
              <p className="text-2xl font-bold">{getAllContacts().filter(function(c) { return c.sales_status === 'interested' || c.sales_status === 'negotiating'; }).length}</p>
              <p className="text-xs text-slate-500">En proceso</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-0">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-6 h-6 mx-auto mb-2 text-emerald-400" />
              <p className="text-2xl font-bold">{getAllContacts().filter(function(c) { return c.sales_status === 'client'; }).length}</p>
              <p className="text-xs text-slate-500">Clientes</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-900 border-slate-800 mb-6">
            <TabsTrigger value="contacts" className="data-[state=active]:bg-emerald-600">
              <Users className="w-4 h-4 mr-2" />Contactos
            </TabsTrigger>
            <TabsTrigger value="assistant" className="data-[state=active]:bg-purple-600">
              <Bot className="w-4 h-4 mr-2" />Asistente IA
            </TabsTrigger>
            <TabsTrigger value="resources" className="data-[state=active]:bg-amber-600">
              <BookOpen className="w-4 h-4 mr-2" />Recursos
            </TabsTrigger>
          </TabsList>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            {/* Filters */}
            <Card className="bg-slate-900 border-0 mb-6">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        placeholder="Buscar por nombre, contacto o ciudad..."
                        value={searchTerm}
                        onChange={function(e) { setSearchTerm(e.target.value); }}
                        className="pl-10 bg-slate-800 border-slate-700"
                      />
                    </div>
                  </div>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700">
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="center">Centros</SelectItem>
                      <SelectItem value="professional">Profesionales</SelectItem>
                      <SelectItem value="workshop">Talleres</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[160px] bg-slate-800 border-slate-700">
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los estados</SelectItem>
                      {STATUS_OPTIONS.map(function(opt) {
                        return <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                  <Select value={filterRegion} onValueChange={setFilterRegion}>
                    <SelectTrigger className="w-[180px] bg-slate-800 border-slate-700">
                      <SelectValue placeholder="Región" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las regiones</SelectItem>
                      {getRegions().map(function(region) {
                        return <SelectItem key={region} value={region}>{region}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Results count */}
            <p className="text-slate-500 text-sm mb-4">
              Mostrando {filteredContacts.length} de {getAllContacts().length} contactos
            </p>

            {/* Contacts List */}
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
              </div>
            ) : (
              <div className="space-y-3">
                {filteredContacts.map(function(contact) {
                  var id = contact.center_id || contact.user_id || contact.workshop_id;
                  var status = contact.sales_status || 'new';
                  var statusColor = getStatusColor(status);
                  var typeIcon = contact.type === 'center' ? Building2 : contact.type === 'professional' ? Users : Sparkles;
                  var TypeIcon = typeIcon;
                  var typeColor = contact.type === 'center' ? 'emerald' : contact.type === 'professional' ? 'blue' : 'purple';
                  
                  return (
                    <Card key={id} className="bg-slate-900 border-0 hover:bg-slate-800/50 transition-all cursor-pointer" onClick={function() { openContactDetail(contact); }}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          {/* Type icon */}
                          <div className={'w-12 h-12 rounded-lg flex items-center justify-center bg-' + typeColor + '-500/20'}>
                            <TypeIcon className={'w-6 h-6 text-' + typeColor + '-400'} />
                          </div>
                          
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-50 truncate">{contact.name}</h3>
                              {contact.is_premium && <span className="text-xs bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">Premium</span>}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-400">
                              {contact.contact_name && (
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />{contact.contact_name}
                                </span>
                              )}
                              {contact.city && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />{contact.city}
                                </span>
                              )}
                              {contact.modality && (
                                <span className="text-emerald-400">{MODALITY_LABELS[contact.modality] || contact.modality}</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Stats */}
                          <div className="hidden md:flex items-center gap-6 text-sm">
                            <div className="text-center">
                              <p className="text-slate-50 font-medium">{contact.views || 0}</p>
                              <p className="text-xs text-slate-500">Visitas</p>
                            </div>
                            <div className="text-center">
                              <p className="text-slate-50 font-medium">{contact.leads || 0}</p>
                              <p className="text-xs text-slate-500">Leads</p>
                            </div>
                          </div>
                          
                          {/* Phone */}
                          {contact.phone && (
                            <a 
                              href={formatWhatsAppLink(contact.phone, contact)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              onClick={function(e) { e.stopPropagation(); }}
                              className="p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 transition-colors"
                            >
                              <MessageCircle className="w-5 h-5 text-green-400" />
                            </a>
                          )}
                          
                          {/* Status badge */}
                          <div className={'px-3 py-1 rounded-full text-xs font-medium bg-' + statusColor + '-500/20 text-' + statusColor + '-400'}>
                            {getStatusLabel(status)}
                          </div>
                          
                          <ChevronRight className="w-5 h-5 text-slate-600" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                
                {filteredContacts.length === 0 && (
                  <Card className="bg-slate-900 border-0">
                    <CardContent className="p-12 text-center">
                      <Search className="w-12 h-12 mx-auto mb-4 text-slate-600" />
                      <p className="text-slate-400">No se encontraron contactos con estos filtros</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* AI Assistant Tab */}
          <TabsContent value="assistant">
            <SalesAssistant />
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources">
            <SalesResources />
          </TabsContent>
        </Tabs>

        {/* Contact Detail Modal */}
        {selectedContact && (
          <ContactDetailModal 
            contact={selectedContact} 
            open={showContactModal} 
            onClose={function() { setShowContactModal(false); setSelectedContact(null); }}
            onUpdate={loadData}
          />
        )}
      </main>
      
      <Footer />
    </div>
  );
}

// Contact Detail Modal Component
function ContactDetailModal({ contact, open, onClose, onUpdate }) {
  var [notes, setNotes] = useState([]);
  var [newNote, setNewNote] = useState('');
  var [status, setStatus] = useState(contact.sales_status || 'new');
  var [contactName, setContactName] = useState(contact.contact_name || '');
  var [loading, setLoading] = useState(false);
  var [savingNote, setSavingNote] = useState(false);
  var [editMode, setEditMode] = useState(false);
  var [sendingCredentials, setSendingCredentials] = useState(false);
  
  // Editable fields for centers
  var [editData, setEditData] = useState({
    name: contact.name || '',
    description: contact.description || '',
    address: contact.address || '',
    city: contact.city || '',
    region: contact.region || '',
    phone: contact.phone || '',
    whatsapp: contact.whatsapp || '',
    email: contact.email || '',
    website: contact.website || '',
    modality: contact.modality || '',
    price_range: contact.price_range || ''
  });

  useEffect(function() {
    if (open && contact) {
      loadNotes();
      setStatus(contact.sales_status || 'new');
      setContactName(contact.contact_name || '');
      // Handle modality - can be array or string
      var modalityValue = contact.modality;
      if (Array.isArray(modalityValue)) {
        modalityValue = modalityValue[0] || '';
      }
      
      setEditData({
        name: contact.name || '',
        description: contact.description || '',
        address: contact.address || '',
        city: contact.city || '',
        region: contact.region || '',
        phone: contact.phone || '',
        whatsapp: contact.whatsapp || '',
        email: contact.email || '',
        website: contact.website || '',
        modality: modalityValue || '',
        price_range: contact.price_range || ''
      });
      setEditMode(false);
    }
  }, [open, contact]);

  async function loadNotes() {
    try {
      var id = contact.center_id || contact.user_id || contact.workshop_id;
      var type = contact.center_id ? 'center' : contact.user_id ? 'professional' : 'workshop';
      var res = await axios.get(API + '/sales/notes/' + type + '/' + id, { headers: getAuthHeaders() });
      setNotes(res.data.notes || []);
    } catch (e) {
      console.error(e);
    }
  }

  async function saveNote() {
    if (!newNote.trim()) return;
    
    setSavingNote(true);
    try {
      var id = contact.center_id || contact.user_id || contact.workshop_id;
      var type = contact.center_id ? 'center' : contact.user_id ? 'professional' : 'workshop';
      await axios.post(API + '/sales/notes/' + type + '/' + id, { note: newNote }, { headers: getAuthHeaders() });
      setNewNote('');
      loadNotes();
      toast.success('Nota guardada');
    } catch (e) {
      toast.error('Error guardando nota');
    }
    setSavingNote(false);
  }

  async function updateStatus(newStatus) {
    setLoading(true);
    try {
      var id = contact.center_id || contact.user_id || contact.workshop_id;
      var type = contact.center_id ? 'center' : contact.user_id ? 'professional' : 'workshop';
      await axios.put(API + '/sales/contact/' + type + '/' + id, { 
        sales_status: newStatus,
        contact_name: contactName
      }, { headers: getAuthHeaders() });
      setStatus(newStatus);
      toast.success('Estado actualizado');
      onUpdate();
    } catch (e) {
      toast.error('Error actualizando');
    }
    setLoading(false);
  }

  async function updateContactName() {
    setLoading(true);
    try {
      var id = contact.center_id || contact.user_id || contact.workshop_id;
      var type = contact.center_id ? 'center' : contact.user_id ? 'professional' : 'workshop';
      await axios.put(API + '/sales/contact/' + type + '/' + id, { 
        sales_status: status,
        contact_name: contactName
      }, { headers: getAuthHeaders() });
      toast.success('Contacto actualizado');
      onUpdate();
    } catch (e) {
      toast.error('Error actualizando');
    }
    setLoading(false);
  }

  async function saveCenterEdit() {
    if (!contact.center_id) return;
    
    setLoading(true);
    try {
      await axios.put(API + '/sales/center/' + contact.center_id + '/edit', editData, { headers: getAuthHeaders() });
      toast.success('Centro actualizado correctamente');
      setEditMode(false);
      onUpdate();
    } catch (e) {
      toast.error('Error actualizando centro');
    }
    setLoading(false);
  }

  async function sendCredentials() {
    if (!contact.center_id) {
      toast.error('Solo puedes enviar credenciales a centros');
      return;
    }
    
    if (!editData.email) {
      toast.error('El centro no tiene email registrado. Agrega un email primero.');
      return;
    }
    
    setSendingCredentials(true);
    try {
      var res = await axios.post(API + '/sales/center/' + contact.center_id + '/send-credentials', {}, { headers: getAuthHeaders() });
      toast.success('Credenciales enviadas a ' + res.data.email_sent_to);
      loadNotes(); // Reload notes to show the credential sent note
      onUpdate();
    } catch (e) {
      var errorMsg = e.response?.data?.detail || 'Error enviando credenciales';
      toast.error(errorMsg);
    }
    setSendingCredentials(false);
  }

  function formatWhatsAppLinkModal(phone) {
    if (!phone) return null;
    var cleaned = phone.replace(/\D/g, '');
    if (!cleaned.startsWith('56')) {
      cleaned = '56' + cleaned;
    }
    
    // If it's a center, include the message
    if (contact && contact.center_id) {
      var slug = contact.slug || contact.center_id;
      var centerUrl = 'https://sinadicciones.org/centros/' + slug;
      
      // Predefined message for sales (no emojis to avoid encoding issues)
      var message = 'Hola, ¿cómo estás? Te escribimos desde SinAdicciones.org, el directorio de centros de rehabilitación y profesionales en Chile.\n\n' +
        'Hemos incluido tu centro en nuestra plataforma para que personas en búsqueda de ayuda te encuentren más rápido.\n\n' +
        'Aquí puedes ver tu ficha: ' + centerUrl + '\n\n' +
        'Si quieres revisar o actualizar tus datos para destacar más información (horarios, servicios, fotos o contacto), podemos ayudarte con eso. ¿Te interesa?';
      
      return 'https://wa.me/' + cleaned + '?text=' + encodeURIComponent(message);
    }
    
    return 'https://wa.me/' + cleaned;
  }

  function handleEditChange(field, value) {
    setEditData(function(prev) {
      return { ...prev, [field]: value };
    });
  }

  if (!open) return null;

  var typeLabel = contact.center_id ? 'Centro' : contact.user_id ? 'Profesional' : 'Taller';
  var isCenter = !!contact.center_id;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xs bg-slate-700 px-2 py-1 rounded">{typeLabel}</span>
              {editMode ? editData.name : contact.name}
            </div>
            {isCenter && (
              <div className="flex items-center gap-2">
                {editMode ? (
                  <>
                    <Button onClick={function() { setEditMode(false); }} variant="ghost" size="sm">
                      <X className="w-4 h-4 mr-1" />Cancelar
                    </Button>
                    <Button onClick={saveCenterEdit} disabled={loading} size="sm" className="bg-emerald-600">
                      <Save className="w-4 h-4 mr-1" />{loading ? 'Guardando...' : 'Guardar'}
                    </Button>
                  </>
                ) : (
                  <Button onClick={function() { setEditMode(true); }} variant="outline" size="sm" className="border-slate-700">
                    <Edit className="w-4 h-4 mr-1" />Editar Info CRM
                  </Button>
                )}
              </div>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Edit Mode for Centers */}
          {editMode && isCenter ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400">Nombre del centro</Label>
                  <Input 
                    value={editData.name}
                    onChange={function(e) { 
                      var newName = e.target.value;
                      var newSlug = newName.toLowerCase()
                        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                        .replace(/[^a-z0-9\s-]/g, '')
                        .replace(/\s+/g, '-')
                        .replace(/-+/g, '-')
                        .trim();
                      if (editData.city) {
                        var citySlug = editData.city.toLowerCase().split(' ')[0];
                        newSlug = newSlug + '-' + citySlug;
                      }
                      handleEditChange('name', newName);
                      handleEditChange('slug', newSlug);
                    }}
                    className="bg-slate-800 border-slate-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-400">Email</Label>
                  <Input 
                    value={editData.email}
                    onChange={function(e) { handleEditChange('email', e.target.value); }}
                    className="bg-slate-800 border-slate-700 mt-1"
                    placeholder="email@centro.cl"
                  />
                </div>
              </div>
              
              {/* Slug field */}
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <Label className="text-blue-400">URL del centro</Label>
                <Input 
                  value={editData.slug || ''}
                  onChange={function(e) { 
                    var value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
                    handleEditChange('slug', value);
                  }}
                  className="bg-slate-800 border-blue-500/30 mt-1 font-mono text-sm"
                  placeholder="se-genera-del-nombre"
                />
                <p className="text-xs text-slate-400 mt-1">
                  URL: <span className="text-emerald-400 font-mono">/centros/{editData.slug || '...'}</span>
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400">Teléfono</Label>
                  <Input 
                    value={editData.phone}
                    onChange={function(e) { handleEditChange('phone', e.target.value); }}
                    className="bg-slate-800 border-slate-700 mt-1"
                    placeholder="+56 9 1234 5678"
                  />
                </div>
                <div>
                  <Label className="text-slate-400">WhatsApp</Label>
                  <Input 
                    value={editData.whatsapp}
                    onChange={function(e) { handleEditChange('whatsapp', e.target.value); }}
                    className="bg-slate-800 border-slate-700 mt-1"
                    placeholder="+56 9 1234 5678"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-400">Dirección</Label>
                <Input 
                  value={editData.address}
                  onChange={function(e) { handleEditChange('address', e.target.value); }}
                  className="bg-slate-800 border-slate-700 mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400">Ciudad</Label>
                  <Input 
                    value={editData.city}
                    onChange={function(e) { handleEditChange('city', e.target.value); }}
                    className="bg-slate-800 border-slate-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-400">Región</Label>
                  <Input 
                    value={editData.region}
                    onChange={function(e) { handleEditChange('region', e.target.value); }}
                    className="bg-slate-800 border-slate-700 mt-1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400">Sitio web</Label>
                  <Input 
                    value={editData.website}
                    onChange={function(e) { handleEditChange('website', e.target.value); }}
                    className="bg-slate-800 border-slate-700 mt-1"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label className="text-slate-400">Modalidad</Label>
                  <Select value={editData.modality} onValueChange={function(v) { handleEditChange('modality', v); }}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residencial">Residencial</SelectItem>
                      <SelectItem value="ambulatorio">Ambulatorio</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="mixto">Mixto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-400">Rango de precios</Label>
                <Input 
                  value={editData.price_range}
                  onChange={function(e) { handleEditChange('price_range', e.target.value); }}
                  className="bg-slate-800 border-slate-700 mt-1"
                  placeholder="$500.000 - $2.000.000 CLP/mes"
                />
              </div>
              
              <div>
                <Label className="text-slate-400">Descripción</Label>
                <Textarea 
                  value={editData.description}
                  onChange={function(e) { handleEditChange('description', e.target.value); }}
                  className="bg-slate-800 border-slate-700 mt-1"
                  rows={3}
                />
              </div>
            </div>
          ) : (
            <>
              {/* Normal View */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400">Nombre del contacto</Label>
                  <div className="flex gap-2 mt-1">
                    <Input 
                      value={contactName} 
                      onChange={function(e) { setContactName(e.target.value); }}
                      placeholder="Nombre de la persona"
                      className="bg-slate-800 border-slate-700"
                    />
                    <Button onClick={updateContactName} disabled={loading} size="sm" className="bg-emerald-600">
                      Guardar
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-400">Teléfono</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-slate-50">{contact.phone || contact.whatsapp || 'No registrado'}</span>
                    {(contact.phone || contact.whatsapp) && (
                      <a 
                        href={formatWhatsAppLinkModal(contact.phone || contact.whatsapp)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30"
                      >
                        <MessageCircle className="w-4 h-4 text-green-400" />
                      </a>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400">Ciudad / Región</Label>
                  <p className="text-slate-50 mt-1">{contact.city || 'No especificada'} {contact.region && '- ' + contact.region}</p>
                </div>
                {contact.modality && (
                  <div>
                    <Label className="text-slate-400">Modalidad</Label>
                    <p className="text-emerald-400 mt-1">{MODALITY_LABELS[contact.modality] || contact.modality}</p>
                  </div>
                )}
              </div>

              {contact.email && (
                <div>
                  <Label className="text-slate-400">Email</Label>
                  <p className="text-slate-50 mt-1">{contact.email}</p>
                </div>
              )}
            </>
          )}

          {/* Send Credentials Button - Only for centers */}
          {isCenter && !editMode && (
            <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-50 font-medium flex items-center gap-2">
                    <Key className="w-4 h-4 text-blue-400" />
                    Enviar Credenciales de Acceso
                  </p>
                  <p className="text-slate-400 text-sm mt-1">
                    {contact.email ? 
                      'Crear cuenta y enviar credenciales a: ' + contact.email :
                      'Primero agrega un email al centro'
                    }
                  </p>
                  {contact.has_account && (
                    <p className="text-amber-400 text-xs mt-1">
                      ⚠️ Este centro ya tiene cuenta creada
                    </p>
                  )}
                </div>
                <Button 
                  onClick={sendCredentials}
                  disabled={sendingCredentials || !contact.email || contact.has_account}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {sendingCredentials ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  {sendingCredentials ? 'Enviando...' : 'Enviar Credenciales'}
                </Button>
              </div>
            </div>
          )}

          {/* Stats */}
          {!editMode && (
            <div className="grid grid-cols-4 gap-4">
              <div className="p-3 bg-slate-800 rounded-lg text-center">
                <p className="text-xl font-bold text-slate-50">{contact.views || 0}</p>
                <p className="text-xs text-slate-500">Visitas</p>
              </div>
              <div className="p-3 bg-slate-800 rounded-lg text-center">
                <p className="text-xl font-bold text-slate-50">{contact.leads || 0}</p>
                <p className="text-xs text-slate-500">Leads</p>
              </div>
              <div className="p-3 bg-slate-800 rounded-lg text-center">
                <p className="text-xl font-bold text-slate-50">{contact.whatsapp_clicks || 0}</p>
                <p className="text-xs text-slate-500">Clics WhatsApp</p>
              </div>
              <div className="p-3 bg-slate-800 rounded-lg text-center">
                <p className="text-xl font-bold text-slate-50">{contact.calls || 0}</p>
                <p className="text-xs text-slate-500">Llamadas</p>
              </div>
            </div>
          )}

          {/* Status */}
          {!editMode && (
            <div>
              <Label className="text-slate-400 mb-2 block">Estado de seguimiento</Label>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map(function(opt) {
                  var isActive = status === opt.value;
                  return (
                    <Button
                      key={opt.value}
                      onClick={function() { updateStatus(opt.value); }}
                      disabled={loading}
                      size="sm"
                      variant={isActive ? 'default' : 'outline'}
                      className={isActive ? 'bg-' + opt.color + '-600' : 'border-slate-700 hover:border-' + opt.color + '-500'}
                    >
                      {opt.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notes */}
          {!editMode && (
            <div>
              <Label className="text-slate-400 mb-2 block">Notas</Label>
              <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
                {notes.length === 0 ? (
                  <p className="text-slate-500 text-sm">No hay notas todavía</p>
                ) : (
                  notes.map(function(note, idx) {
                    return (
                      <div key={idx} className="p-3 bg-slate-800 rounded-lg">
                        <p className="text-slate-50 text-sm">{note.note}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(note.created_at).toLocaleString('es-CL')} - {note.created_by}
                        </p>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="flex gap-2">
                <Textarea
                  value={newNote}
                  onChange={function(e) { setNewNote(e.target.value); }}
                  placeholder="Agregar una nota..."
                  className="bg-slate-800 border-slate-700"
                  rows={2}
                />
                <Button onClick={saveNote} disabled={savingNote} className="bg-emerald-600">
                  {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          )}

          {/* Subscription info */}
          {!editMode && (
            <div className="p-4 bg-slate-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Suscripción</p>
                  <p className={'font-medium ' + (contact.is_premium ? 'text-amber-400' : 'text-slate-50')}>
                    {contact.is_premium ? 'Premium' : 'Gratuito'}
                  </p>
                </div>
                {!contact.is_premium && (
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
                    <Star className="w-4 h-4 mr-1" />Ofrecer Premium
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Sales AI Assistant Component
function SalesAssistant() {
  var [messages, setMessages] = useState([
    { role: 'assistant', content: '¡Hola! Soy tu asistente de ventas. Puedo ayudarte con:\n\n• Técnicas de venta para centros de rehabilitación\n• Información sobre la plataforma SinAdicciones\n• Consejos para cerrar ventas\n• Responder dudas sobre adicciones y tratamientos\n\n¿En qué puedo ayudarte?' }
  ]);
  var [input, setInput] = useState('');
  var [loading, setLoading] = useState(false);

  async function sendMessage() {
    if (!input.trim() || loading) return;
    
    var userMessage = input.trim();
    setInput('');
    setMessages(function(prev) { return prev.concat([{ role: 'user', content: userMessage }]); });
    
    setLoading(true);
    try {
      var res = await axios.post(API + '/sales/assistant', { message: userMessage }, { headers: getAuthHeaders() });
      setMessages(function(prev) { return prev.concat([{ role: 'assistant', content: res.data.response }]); });
    } catch (e) {
      toast.error('Error al obtener respuesta');
      setMessages(function(prev) { return prev.concat([{ role: 'assistant', content: 'Lo siento, hubo un error. Por favor intenta de nuevo.' }]); });
    }
    setLoading(false);
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  return (
    <Card className="bg-slate-900 border-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-purple-400" />
          Asistente de Ventas IA
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Chat messages */}
        <div className="h-96 overflow-y-auto mb-4 space-y-4 p-4 bg-slate-800 rounded-lg">
          {messages.map(function(msg, idx) {
            return (
              <div key={idx} className={'flex ' + (msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                <div className={'max-w-[80%] p-3 rounded-lg ' + (msg.role === 'user' ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-50')}>
                  <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                </div>
              </div>
            );
          })}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-700 p-3 rounded-lg">
                <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
              </div>
            </div>
          )}
        </div>
        
        {/* Input */}
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={function(e) { setInput(e.target.value); }}
            onKeyPress={handleKeyPress}
            placeholder="Escribe tu pregunta... (Enter para enviar)"
            className="bg-slate-800 border-slate-700"
            rows={2}
          />
          <Button onClick={sendMessage} disabled={loading} className="bg-purple-600 hover:bg-purple-700">
            <Send className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Quick prompts */}
        <div className="flex flex-wrap gap-2 mt-4">
          <Button size="sm" variant="outline" className="border-slate-700 text-xs" onClick={function() { setInput('¿Cómo abordar a un centro que no responde?'); }}>
            Centro no responde
          </Button>
          <Button size="sm" variant="outline" className="border-slate-700 text-xs" onClick={function() { setInput('¿Cuáles son los beneficios del plan Premium?'); }}>
            Beneficios Premium
          </Button>
          <Button size="sm" variant="outline" className="border-slate-700 text-xs" onClick={function() { setInput('¿Cómo manejar objeciones de precio?'); }}>
            Objeciones de precio
          </Button>
          <Button size="sm" variant="outline" className="border-slate-700 text-xs" onClick={function() { setInput('Dame un pitch de 30 segundos para centros'); }}>
            Pitch rápido
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Sales Resources Component
function SalesResources() {
  var resources = [
    {
      category: 'Técnicas de Venta',
      icon: Target,
      color: 'emerald',
      items: [
        { title: 'Pitch de apertura (30 segundos)', content: '"Hola, soy [nombre] de SinAdicciones.org, el directorio #1 de centros de rehabilitación en Chile. Ayudamos a centros como el suyo a recibir más pacientes calificados. ¿Tiene 2 minutos para contarle cómo?"' },
        { title: 'Manejo de "No tengo tiempo"', content: 'Respuesta: "Entiendo perfectamente, los centros están muy ocupados. Por eso mismo llamaba - nuestra plataforma trabaja para ustedes 24/7 atrayendo pacientes mientras ustedes se enfocan en su labor. ¿Cuándo sería mejor momento para una llamada de 5 minutos?"' },
        { title: 'Manejo de "Ya tenemos pacientes"', content: 'Respuesta: "¡Excelente! Eso habla muy bien de su trabajo. La pregunta es: ¿están llegando los pacientes ideales para su especialidad? Nuestros centros Premium reciben leads pre-calificados según su modalidad y ubicación."' },
        { title: 'Cierre de venta', content: '"Basado en lo que me cuenta, el plan Premium sería ideal para [centro]. Con 35% de comisión solo por pacientes que efectivamente se tratan, no hay riesgo para ustedes. ¿Empezamos este mes?"' }
      ]
    },
    {
      category: 'Beneficios de la Plataforma',
      icon: Star,
      color: 'amber',
      items: [
        { title: 'Plan Gratuito', content: '• Perfil básico visible en el directorio\n• Aparecer en búsquedas\n• Recibir consultas de contacto\n• Estadísticas básicas' },
        { title: 'Plan Premium', content: '• Posición destacada en búsquedas\n• Badge de verificación\n• Más fotos y videos\n• Estadísticas avanzadas\n• Leads pre-calificados\n• Soporte prioritario' },
        { title: 'Plan por Efectividad', content: '• 35% de comisión por paciente tratado\n• Sin costo fijo mensual\n• Solo pagas por resultados\n• Tracking completo de conversiones' },
        { title: 'Estadísticas que reciben', content: '• Visitas al perfil\n• Clics en WhatsApp y teléfono\n• Consultas recibidas\n• Comparación con competencia\n• Tendencias mensuales' }
      ]
    },
    {
      category: 'Información de Adicciones',
      icon: BookOpen,
      color: 'blue',
      items: [
        { title: 'Tipos de tratamiento', content: '• Residencial: Internación 24/7 (1-12 meses)\n• Ambulatorio: Sesiones programadas sin internación\n• Hospital de día: Tratamiento intensivo de día\n• Online: Terapia a distancia' },
        { title: 'Adicciones más comunes en Chile', content: '• Alcohol (más prevalente)\n• Cocaína y pasta base\n• Marihuana\n• Medicamentos (benzodiacepinas)\n• Ludopatía (en aumento)' },
        { title: 'Precios promedio', content: '• Residencial: $350.000 - $3.000.000/mes\n• Ambulatorio: $350.000 - $600.000/mes\n• Online: desde $200.000/mes' },
        { title: 'Lo que buscan las familias', content: '• Precio transparente\n• Opiniones de otros pacientes\n• Ubicación cercana\n• Especialización en su adicción\n• Resultados comprobables' }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      {resources.map(function(category) {
        var Icon = category.icon;
        return (
          <Card key={category.category} className="bg-slate-900 border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Icon className={'w-5 h-5 text-' + category.color + '-400'} />
                {category.category}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {category.items.map(function(item, idx) {
                  return (
                    <div key={idx} className="p-4 bg-slate-800 rounded-lg">
                      <h4 className="font-medium text-slate-50 mb-2">{item.title}</h4>
                      <p className="text-sm text-slate-400 whitespace-pre-wrap">{item.content}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default SalesDashboard;
